/**
 * Version Storage Utility
 * Manages localStorage operations for version tracking
 * Storage key: splister_last_read_version
 */

const STORAGE_KEY = 'splister_last_read_version';

/**
 * Get the last read version from localStorage
 * Returns null if no value has been stored
 *
 * @returns Last read version string (e.g., "1.3.0") or null if not set
 */
export function getLastReadVersion(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value;
  } catch (error) {
    // localStorage unavailable (private browsing, quota exceeded, etc.)
    console.warn('Failed to read from localStorage:', error);
    return null;
  }
}

/**
 * Mark a specific version as read in localStorage
 * Updates the last read version value
 *
 * @param version - Version string to mark as read (e.g., "1.2.0")
 */
export function markVersionAsRead(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, version);
  } catch (error) {
    // localStorage unavailable
    console.warn('Failed to write to localStorage:', error);
  }
}

/**
 * Mark all versions in a list as read
 * Stores the highest version (first in descending list)
 * All versions <= this version are implicitly considered read
 *
 * @param versions - Array of VersionItem objects to mark as read
 */
export function markAllAsRead(versions: Array<{ version: string }>): void {
  if (versions.length === 0) {
    return;
  }

  try {
    // Get highest version (first in descending list)
    // Safe because we checked length > 0 above
    const highestVersion = versions[0]!.version;

    localStorage.setItem(STORAGE_KEY, highestVersion);
  } catch (error) {
    // localStorage unavailable
    console.warn('Failed to write to localStorage:', error);
  }
}
